import {
	Checkbox,
	FormControlLabel,
	FormGroup,
	InputLabel,
	MenuItem,
	Select,
	ToggleButton,
	ToggleButtonGroup,
} from "@mui/material";
import { useState } from "react";
import { Info } from "lucide-react";
import {
	PdfTemplateConfiguration,
	PdfTemplateConfigurationContextProps,
	usePdfTemplateConfigurationContext,
} from "../../context/PdfTemplateConfigurationContext";
import { PdfTemplateType } from "../../pdf-templates/document-template.types";
import { CustomTextFieldWithSaveButton } from "../../forms/form-input-components/custom-input-components/custom-text-field-with-save-button.component";
import { Alignment } from "../../pdf-templates/pdf-templates/types";
import { PDFRtf } from "./pdf-rtf.component";
import {
	PDFConfigPlaceholder,
	Region,
	SammelrechnungTitleDateMode,
} from "../../types/enums";
import { REGION_METADATA } from "../../types/region.types";
import { useCentralStore } from "../../store/Central";
import { resolveRegionKind } from "../../lib/utils/region-utils";
import { TooltipWrapper } from "../../components/ui/tooltip-wrapper";
import { ReminderTemplateType } from "../debitoren/mahnwesen/utils/reminder-stage";

// frankfurter.dev supported ISO currency codes (base/goal for the #WAEHRUNGSRECHNER converter).
const FRANKFURTER_CURRENCY_CODES = [
	"AUD",
	"BGN",
	"BRL",
	"CAD",
	"CHF",
	"CNY",
	"CZK",
	"DKK",
	"EUR",
	"GBP",
	"HKD",
	"HUF",
	"IDR",
	"ILS",
	"INR",
	"ISK",
	"JPY",
	"KRW",
	"MXN",
	"MYR",
	"NOK",
	"NZD",
	"PHP",
	"PLN",
	"RON",
	"SEK",
	"SGD",
	"THB",
	"TRY",
	"USD",
	"ZAR",
];

// Placeholders that can be inserted into a (region-specific) document comment.
// `display` is what the legend shows, `insert` is what the insert button adds to the
// editor. `requiresCrossRegion` hides EORI unless the lab operates across regions (a
// client can be in a different region than the lab), since EORI is cross-border only.
const COMMENT_PLACEHOLDERS: {
	display: string;
	insert: string;
	label: string;
	description: string;
	info?: string;
	requiresCrossRegion?: boolean;
}[] = [
	{
		display: PDFConfigPlaceholder.EORI,
		insert: PDFConfigPlaceholder.EORI,
		label: "EORI-Nr.",
		description: "EORI-Nummer des Kunden",
		info: "Zoll-Identifikationsnummer für grenzüberschreitende EU-Sendungen. Pro Kunde hinterlegt, optional.",
		requiresCrossRegion: true,
	},
	{
		display: `${PDFConfigPlaceholder.CURRENCY_CONVERTER}:BASIS:ZIEL`,
		insert: `${PDFConfigPlaceholder.CURRENCY_CONVERTER}:CHF:EUR`,
		label: "Umrechnung",
		description: "Total in Zielwährung umrechnen",
		info: `Codes (ISO): ${FRANKFURTER_CURRENCY_CODES.join(
			", "
		)}. Kurse: tägliche EZB-Referenzkurse über frankfurter.dev, ohne Gewähr.`,
	},
];

// Override pdfTemplateConfiguration: PdfTemplateConfiguration | undefined; to not be undefined
interface SingleConfigurationProps
	extends PdfTemplateConfigurationContextProps {
	pdfTemplateConfiguration: PdfTemplateConfiguration;
}

export const PdfTemplateSingleConfigurations: React.FC<{
	pdfTemplateType: PdfTemplateType;
}> = ({ pdfTemplateType }) => {
	// Pass the entire context as props
	const props: PdfTemplateConfigurationContextProps =
		usePdfTemplateConfigurationContext();
	if (!props.pdfTemplateConfiguration) {
		return null;
	}

	switch (pdfTemplateType) {
		case PdfTemplateType.KOSTENVORANSCHLAG:
			return (
				<KostenvoranschlagSettings
					{...(props as SingleConfigurationProps)}
				/>
			);
		case PdfTemplateType.LIEFERSCHEIN:
			return (
				<LieferscheinSettings
					{...(props as SingleConfigurationProps)}
				/>
			);
		case PdfTemplateType.GUTSCHRIFT:
			return (
				<GutschriftSettings {...(props as SingleConfigurationProps)} />
			);
		case PdfTemplateType.RECHNUNG:
			return (
				<RechnungSettings {...(props as SingleConfigurationProps)} />
			);
		case PdfTemplateType.SAMMELRECHNUNG:
			return (
				<SammelrechnungSettings
					{...(props as SingleConfigurationProps)}
				/>
			);
		case PdfTemplateType.KARTENZAHLUNGSQUITTUNG:
			return (
				<KartenzahlungsquittungSettings
					{...(props as SingleConfigurationProps)}
				/>
			);
		case PdfTemplateType.BARZAHLUNGSQUITTUNG:
			return (
				<BarzahlungsquittungSettings
					{...(props as SingleConfigurationProps)}
				/>
			);
		case PdfTemplateType.AKONTORECHNUNG:
			return (
				<AkontorechnungSettings
					{...(props as SingleConfigurationProps)}
				/>
			);
		case PdfTemplateType.ZAHLUNGSERINNERUNG:
		case PdfTemplateType.ERSTE_MAHNUNG:
		case PdfTemplateType.FOLGEMAHNUNG:
		case PdfTemplateType.LETZTE_MAHNUNG:
			return (
				<PaymentReminderSettings
					pdfTemplateType={pdfTemplateType}
					{...(props as SingleConfigurationProps)}
				/>
			);
		case PdfTemplateType.AUFTRAGSBLATT:
			return (
				<AuftragsblattSettings
					{...(props as SingleConfigurationProps)}
				/>
			);
		case PdfTemplateType.ETIK_AUFTRAG:
			return (
				<EtikAuftragSettings {...(props as SingleConfigurationProps)} />
			);
		default:
			return null;
	}
};

export const KostenvoranschlagSettings: React.FC<SingleConfigurationProps> = (
	props
) => {
	return (
		<div>
			<FormGroup>
				<FormControlLabel
					control={
						<Checkbox
							checked={
								props.pdfTemplateConfiguration[
									PdfTemplateType.KOSTENVORANSCHLAG
								].showSignature
							}
							onChange={(e) => {
								props.updatePdfTemplateConfiguration(
									PdfTemplateType.KOSTENVORANSCHLAG,
									{
										showSignature: e.target.checked,
									}
								);
							}}
						/>
					}
					label="Unterschrift anzeigen"
				/>
			</FormGroup>
			<FooterTextSettings
				pdfTemplateType={PdfTemplateType.KOSTENVORANSCHLAG}
				{...props}
			/>
			<CommentTextSettings
				pdfTemplateType={PdfTemplateType.KOSTENVORANSCHLAG}
				{...props}
			/>
		</div>
	);
};

export const LieferscheinSettings: React.FC<SingleConfigurationProps> = (
	props
) => {
	return (
		<div>
			<FooterTextSettings
				pdfTemplateType={PdfTemplateType.LIEFERSCHEIN}
				{...props}
			/>
			<CommentTextSettings
				pdfTemplateType={PdfTemplateType.LIEFERSCHEIN}
				{...props}
			/>
			<TaxAmountTextSettings
				pdfTemplateType={PdfTemplateType.LIEFERSCHEIN}
				{...props}
			/>
		</div>
	);
};

export const GutschriftSettings: React.FC<SingleConfigurationProps> = (
	props
) => {
	return (
		<div>
			<FooterTextSettings
				pdfTemplateType={PdfTemplateType.GUTSCHRIFT}
				{...props}
			/>
			<CommentTextSettings
				pdfTemplateType={PdfTemplateType.GUTSCHRIFT}
				{...props}
			/>
		</div>
	);
};

export const RechnungSettings: React.FC<SingleConfigurationProps> = (props) => {
	return (
		<div>
			<FooterTextSettings
				pdfTemplateType={PdfTemplateType.RECHNUNG}
				{...props}
			/>
			<CommentTextSettings
				pdfTemplateType={PdfTemplateType.RECHNUNG}
				{...props}
			/>
			<TaxAmountTextSettings
				pdfTemplateType={PdfTemplateType.RECHNUNG}
				{...props}
			/>
			<FormattingOverrideSettings
				pdfTemplateType={PdfTemplateType.RECHNUNG}
				{...props}
			/>
			<CustomTextFieldWithSaveButton
				label="Text oberhalb des Einzahlungsscheins"
				value={
					props.pdfTemplateConfiguration[PdfTemplateType.RECHNUNG]
						.textAbovePaymentSlip
				}
				validationError={null}
				multiline
				rows={4}
				fullWidth
				onSave={(value) => {
					props.updatePdfTemplateConfiguration(
						PdfTemplateType.RECHNUNG,
						{
							textAbovePaymentSlip: value,
						}
					);
				}}
				onReset={() => {
					props.resetConfigurationColumn(
						PdfTemplateType.RECHNUNG,
						"textAbovePaymentSlip"
					);
				}}
			/>
		</div>
	);
};

export const SammelrechnungSettings: React.FC<SingleConfigurationProps> = (
	props
) => {
	return (
		<div>
			<FooterTextSettings
				pdfTemplateType={PdfTemplateType.SAMMELRECHNUNG}
				{...props}
			/>
			<CommentTextSettings
				pdfTemplateType={PdfTemplateType.SAMMELRECHNUNG}
				{...props}
			/>
			<TaxAmountTextSettings
				pdfTemplateType={PdfTemplateType.SAMMELRECHNUNG}
				{...props}
			/>
			<FormattingOverrideSettings
				pdfTemplateType={PdfTemplateType.SAMMELRECHNUNG}
				{...props}
			/>
			<CustomTextFieldWithSaveButton
				label="Text oberhalb des Einzahlungsscheins"
				value={
					props.pdfTemplateConfiguration[
						PdfTemplateType.SAMMELRECHNUNG
					].textAbovePaymentSlip
				}
				validationError={null}
				multiline
				rows={4}
				fullWidth
				onSave={(value) => {
					props.updatePdfTemplateConfiguration(
						PdfTemplateType.SAMMELRECHNUNG,
						{
							textAbovePaymentSlip: value,
						}
					);
				}}
				onReset={() => {
					props.resetConfigurationColumn(
						PdfTemplateType.SAMMELRECHNUNG,
						"textAbovePaymentSlip"
					);
				}}
			/>
		</div>
	);
};

export const KartenzahlungsquittungSettings: React.FC<
	SingleConfigurationProps
> = (props) => {
	return (
		<div>
			<FooterTextSettings
				pdfTemplateType={PdfTemplateType.KARTENZAHLUNGSQUITTUNG}
				{...props}
			/>
			<CommentTextSettings
				pdfTemplateType={PdfTemplateType.KARTENZAHLUNGSQUITTUNG}
				{...props}
			/>
		</div>
	);
};

export const BarzahlungsquittungSettings: React.FC<SingleConfigurationProps> = (
	props
) => {
	return (
		<div>
			<FooterTextSettings
				pdfTemplateType={PdfTemplateType.BARZAHLUNGSQUITTUNG}
				{...props}
			/>
			<CommentTextSettings
				pdfTemplateType={PdfTemplateType.BARZAHLUNGSQUITTUNG}
				{...props}
			/>
		</div>
	);
};

export const AkontorechnungSettings: React.FC<SingleConfigurationProps> = (
	props
) => {
	return (
		<div>
			<FooterTextSettings
				pdfTemplateType={PdfTemplateType.AKONTORECHNUNG}
				{...props}
			/>
			<CommentTextSettings
				pdfTemplateType={PdfTemplateType.AKONTORECHNUNG}
				{...props}
			/>
			<CustomTextFieldWithSaveButton
				label="Text oberhalb des Einzahlungsscheins"
				value={
					props.pdfTemplateConfiguration[
						PdfTemplateType.AKONTORECHNUNG
					].textAbovePaymentSlip
				}
				validationError={null}
				multiline
				rows={4}
				fullWidth
				onSave={(value) => {
					props.updatePdfTemplateConfiguration(
						PdfTemplateType.AKONTORECHNUNG,
						{
							textAbovePaymentSlip: value,
						}
					);
				}}
				onReset={() => {
					props.resetConfigurationColumn(
						PdfTemplateType.AKONTORECHNUNG,
						"textAbovePaymentSlip"
					);
				}}
			/>
		</div>
	);
};

export const PaymentReminderSettings: React.FC<
	SingleConfigurationProps & { pdfTemplateType: ReminderTemplateType }
> = ({ pdfTemplateType, ...props }) => {
	return (
		<div>
			<FooterTextSettings pdfTemplateType={pdfTemplateType} {...props} />
			<CommentTextSettings pdfTemplateType={pdfTemplateType} {...props} />
		</div>
	);
};

export const AuftragsblattSettings: React.FC<SingleConfigurationProps> = (
	props
) => {
	return <div></div>;
};

export const EtikAuftragSettings: React.FC<SingleConfigurationProps> = ({
	pdfTemplateConfiguration,
	updatePdfTemplateConfiguration,
}) => {
	return (
		<div className="flex flex-col gap-2">
			<FormControlLabel
				control={
					<Checkbox
						checked={
							pdfTemplateConfiguration?.[
								PdfTemplateType.ETIK_AUFTRAG
							]?.showToothColor ?? false
						}
						onChange={(e) => {
							updatePdfTemplateConfiguration(
								PdfTemplateType.ETIK_AUFTRAG,
								{
									showToothColor: e.target.checked,
								}
							);
						}}
					/>
				}
				label="Zahnfarbe anzeigen"
			/>
		</div>
	);
};

const FooterTextSettings: React.FC<
	SingleConfigurationProps & { pdfTemplateType: PdfTemplateType }
> = ({
	pdfTemplateConfiguration,
	updatePdfTemplateConfiguration,
	pdfTemplateType,
}) => {
	return (
		<PDFRtf
			value={pdfTemplateConfiguration[pdfTemplateType].footerText}
			controls={[
				[
					"italic",
					"underline",
					"bold",
					"alignLeft",
					"alignCenter",
					"alignRight",
				],
				["h1", "h2", "h3", "h4", "h5", "h6"],
				["image"],
			]}
			onSave={(val: string, width: number) => {
				updatePdfTemplateConfiguration(pdfTemplateType, {
					footerText: {
						text: val,
						width,
					},
				});
			}}
		/>
	);
};

const CommentTextSettings: React.FC<
	SingleConfigurationProps & { pdfTemplateType: PdfTemplateType }
> = ({
	pdfTemplateConfiguration,
	updatePdfTemplateConfiguration,
	pdfTemplateType,
}) => {
	const activatedRegions = useCentralStore((state) => state.activatedRegions);
	const organization = useCentralStore((state) => state.organization);
	const [selectedRegion, setSelectedRegion] = useState<Region | "default">(
		"default"
	);

	const templateConfig = pdfTemplateConfiguration[pdfTemplateType];

	// EORI is an EU cross-border concept — only relevant when the lab can serve clients
	// in a different region than its own (i.e. has a region other than the org's).
	const orgRegion = resolveRegionKind(null, organization, activatedRegions);
	const hasCrossRegion =
		orgRegion != null &&
		activatedRegions.some((region) => region.region !== orgRegion);
	const visiblePlaceholders = COMMENT_PLACEHOLDERS.filter(
		(placeholder) => !placeholder.requiresCrossRegion || hasCrossRegion
	);

	const commentValue =
		selectedRegion === "default"
			? templateConfig.commentText
			: templateConfig.commentTextByRegion?.[selectedRegion];

	const handleSaveComment = (val: string, width: number) => {
		if (selectedRegion === "default") {
			updatePdfTemplateConfiguration(pdfTemplateType, {
				commentText: { text: val, width },
			});
		} else {
			updatePdfTemplateConfiguration(pdfTemplateType, {
				commentTextByRegion: {
					...(templateConfig.commentTextByRegion ?? {}),
					[selectedRegion]: { text: val, width },
				},
			});
		}
	};

	return (
		<div>
			<div className="flex mb-6 flex-col gap-2">
				<InputLabel id="COmment alignment">
					Kommentar-Ausrichtung
				</InputLabel>

				<ToggleButtonGroup
					color="primary"
					value={pdfTemplateConfiguration?.general.commentAlignment}
					exclusive
					onChange={(_, v) => {
						updatePdfTemplateConfiguration("general", {
							commentAlignment: v as Alignment,
						});
					}}
					aria-label="Platform"
				>
					<ToggleButton value="left">Links</ToggleButton>
					<ToggleButton value="center">Zentriert</ToggleButton>
					<ToggleButton value="right">Rechts</ToggleButton>
				</ToggleButtonGroup>
			</div>

			{activatedRegions.length > 0 && (
				<div className="flex mb-4 flex-col gap-2">
					<InputLabel id="comment-region">
						Kommentar-Region
					</InputLabel>
					<Select
						size="small"
						className="w-[350px]"
						value={String(selectedRegion)}
						onChange={(event) => {
							const value = event.target.value;
							setSelectedRegion(
								value === "default"
									? "default"
									: (Number(value) as Region)
							);
						}}
						aria-label="Kommentar-Region"
						data-testid="comment-region-select"
					>
						<MenuItem value="default">
							Standard (alle Regionen)
						</MenuItem>
						{activatedRegions.map((region) => (
							<MenuItem
								key={region.id}
								value={String(region.region)}
							>
								{REGION_METADATA[region.region]?.label ??
									`Region ${region.region}`}
							</MenuItem>
						))}
					</Select>
				</div>
			)}

			<div className="mb-3 flex flex-col gap-1 text-xs">
				<span className="font-semibold text-gray-700">Platzhalter</span>
				{visiblePlaceholders.map((placeholder) => (
					<div
						key={placeholder.display}
						className="flex items-center gap-2"
					>
						<code className="whitespace-nowrap text-gray-800">
							{placeholder.display}
						</code>
						<span className="text-gray-500">
							{placeholder.description}
						</span>
						{placeholder.info && (
							<TooltipWrapper title={placeholder.info}>
								<button
									type="button"
									aria-label={`Info zu ${placeholder.label}`}
									className="text-gray-400 hover:text-gray-600"
								>
									<Info className="h-3.5 w-3.5" />
								</button>
							</TooltipWrapper>
						)}
					</div>
				))}
			</div>

			<PDFRtf
				key={String(selectedRegion)}
				value={commentValue}
				controls={[
					[
						"italic",
						"underline",
						"bold",
						"alignLeft",
						"alignCenter",
						"alignRight",
					],
					["h1", "h2", "h3", "h4", "h5", "h6"],
					["image"],
				]}
				placeholders={visiblePlaceholders.map((placeholder) => ({
					token: placeholder.insert,
					label: placeholder.label,
				}))}
				onSave={handleSaveComment}
			/>
		</div>
	);
};

const TaxAmountTextSettings: React.FC<
	SingleConfigurationProps & { pdfTemplateType: PdfTemplateType }
> = ({
	pdfTemplateConfiguration,
	updatePdfTemplateConfiguration,
	resetConfigurationColumn,
	pdfTemplateType,
}) => {
	// Only show for templates that have tax amount text
	const templateConfig = pdfTemplateConfiguration[pdfTemplateType] as any;
	if (!("taxAmountText" in templateConfig)) {
		return null;
	}

	return (
		<div>
			<CustomTextFieldWithSaveButton
				label="Steuerausweisung"
				value={templateConfig.taxAmountText || ""}
				helperText={
					<div className="text-black font-bold">
						Verfügbare Platzhalter:
						<div className="flex flex-row text-xs">
							<div className="font-bold">
								<p>{PDFConfigPlaceholder.ZAZTAX}:</p>
								<p>{PDFConfigPlaceholder.ZAZFREE}:</p>
								<p>{PDFConfigPlaceholder.ZAZTAXSTATUS}:</p>
								<p>{PDFConfigPlaceholder.CURRENCY}:</p>
							</div>
							<div className="ml-2 font-normal">
								<p>
									Wird ersetzt durch den steuerpflichtigen
									Betrag
								</p>
								<p>
									Wird ersetzt durch den steuerfreien Betrag
								</p>
								<p>Wird ersetzt durch "inkl. / exkl. MwSt."</p>
								<p>
									Wird ersetzt durch die Währung (z.B. CHF,
									EUR)
								</p>
							</div>
						</div>
					</div>
				}
				validationError={null}
				multiline
				rows={3}
				fullWidth
				onSave={(value) => {
					updatePdfTemplateConfiguration(pdfTemplateType, {
						taxAmountText: value,
					});
				}}
				onReset={() => {
					resetConfigurationColumn(pdfTemplateType, "taxAmountText");
				}}
			/>

			<FormControlLabel
				control={
					<Checkbox
						checked={
							templateConfig.showZazInclusiveAmounts || false
						}
						onChange={(event) => {
							updatePdfTemplateConfiguration(pdfTemplateType, {
								showZazInclusiveAmounts: event.target.checked,
							});
						}}
					/>
				}
				label="Angaben inklusive MwSt."
				style={{ marginTop: "8px" }}
			/>
		</div>
	);
};

const FormattingOverrideSettings: React.FC<
	SingleConfigurationProps & { pdfTemplateType: PdfTemplateType }
> = ({
	pdfTemplateConfiguration,
	updatePdfTemplateConfiguration,
	pdfTemplateType,
}) => {
	const templateConfig = pdfTemplateConfiguration[pdfTemplateType];
	const generalConfig = pdfTemplateConfiguration.general;

	return (
		<div style={{ marginTop: "16px" }}>
			<InputLabel style={{ marginBottom: "8px", fontWeight: "bold" }}>
				Formatierung für dieses Dokument deaktivieren
			</InputLabel>
			<FormGroup>
				<FormControlLabel
					control={
						<Checkbox
							checked={
								templateConfig.overrideShowTotalCurrency ||
								false
							}
							onChange={(event) => {
								updatePdfTemplateConfiguration(
									pdfTemplateType,
									{
										overrideShowTotalCurrency:
											event.target.checked,
									}
								);
							}}
						/>
					}
					label={`Währung bei Total deaktivieren (global: ${
						generalConfig.showTotalCurrency ? "AN" : "AUS"
					})`}
				/>
			</FormGroup>
		</div>
	);
};

export const TitleDateModeSettings: React.FC<SingleConfigurationProps> = ({
	pdfTemplateConfiguration,
	updatePdfTemplateConfiguration,
}) => {
	const templateConfig =
		pdfTemplateConfiguration[PdfTemplateType.SAMMELRECHNUNG];

	return (
		<div className="flex mb-6 flex-col gap-2">
			<InputLabel>Datum im Titel</InputLabel>
			<ToggleButtonGroup
				color="primary"
				value={
					templateConfig.titleDateMode ||
					SammelrechnungTitleDateMode.JOB_DATE_RANGE
				}
				exclusive
				onChange={(_, value) => {
					if (value !== null) {
						updatePdfTemplateConfiguration(
							PdfTemplateType.SAMMELRECHNUNG,
							{
								titleDateMode:
									value as SammelrechnungTitleDateMode,
							}
						);
					}
				}}
				aria-label="Zeitraum im Titel"
			>
				<ToggleButton
					value={SammelrechnungTitleDateMode.JOB_DATE_RANGE}
				>
					<div
						style={{
							textAlign: "center",
							fontSize: "0.875rem",
							padding: "2px",
						}}
					>
						<div
							style={{ fontWeight: "bold", marginBottom: "2px" }}
						>
							Zeitraum der Aufträge
						</div>
						<div
							style={{
								fontSize: "0.7rem",
								opacity: 0.85,
								lineHeight: "1.3",
							}}
						>
							Ein Monat: Monatsrechnung Jan. 2024
							<br />
							Mehrere Monate: Sammelrechnung Jan. - Apr. 2024
							<br />
							Verschiedene Jahre: Sammelrechnung Dez. 2023 - Feb.
							2024
						</div>
					</div>
				</ToggleButton>
				<ToggleButton value={SammelrechnungTitleDateMode.BILLING_DATE}>
					<div
						style={{
							textAlign: "center",
							fontSize: "0.875rem",
							padding: "2px",
						}}
					>
						<div
							style={{ fontWeight: "bold", marginBottom: "2px" }}
						>
							Rechnungsdatum
						</div>
						<div
							style={{
								fontSize: "0.7rem",
								opacity: 0.85,
								lineHeight: "1.3",
							}}
						>
							Immer Monatsrechnung [Monat] [Jahr]
							<br />
							z.B. Monatsrechnung Apr. 2024
							<br />
							<span
								style={{
									fontSize: "0.65rem",
									fontStyle: "italic",
								}}
							>
								(Bei mehreren Monaten wird nur der
								Rechnungsmonat angezeigt)
							</span>
						</div>
					</div>
				</ToggleButton>
			</ToggleButtonGroup>
		</div>
	);
};
